'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import gsap from 'gsap';
import { useIsomorphicLayoutEffect } from 'hooks/dom/useIsomorphicLayoutEffect';
import { useVariantSelection } from './VariantSelectionContext';
import { Heading } from './styleConstants';
import { ContainerLG } from 'components/shared/Container';
import { getCompressedAssetUrl } from 'lib/config/assetUrls';
import styles from './ProductDetails.module.css';

const cx = (...parts: (string | false | undefined)[]) =>
  parts.filter(Boolean).join(' ');

const ANIMATION_DURATION = 0.3;
const SWAP_EXIT_DURATION = 0.25;
const SWAP_ENTER_DURATION = 0.4;

const CLOSED_HEIGHT = 0;
const OPEN_PADDING_BOTTOM_DESKTOP = 32;
const OPEN_PADDING_BOTTOM_MOBILE = 16.99;
const DESKTOP_BREAKPOINT = 768;
const HEIGHT_BUFFER = 20;

const SWAP_EXIT_SCALE = 0.8;
const SWAP_ENTER_SCALE_START = 1.2;
const SWAP_ENTER_SCALE_END = 1.0;
const SWAP_EXIT_X_OFFSET = -150;
const SWAP_ENTER_X_OFFSET = 250;

interface AnimatedSwapProps {
  children: React.ReactElement;
  swapKey: string | number | null;
  className?: string;
}

const AnimatedSwap: React.FC<AnimatedSwapProps> = ({
  children,
  swapKey,
  className,
}) => {
  const [currentChildren, setCurrentChildren] =
    useState<React.ReactElement>(children);
  const [oldChildren, setOldChildren] = useState<React.ReactElement | null>(
    null,
  );
  const oldLayerRef = useRef<HTMLDivElement>(null);
  const newLayerRef = useRef<HTMLDivElement>(null);
  const previousKeyRef = useRef<string | number | null>(null);
  const previousChildrenRef = useRef<React.ReactElement>(children);
  const isAnimatingRef = useRef(false);
  const timelineRef = useRef<gsap.core.Timeline | null>(null);
  const shouldAnimateRef = useRef(false);

  const resetToFinalState = useCallback((element: HTMLElement | null) => {
    if (element) {
      gsap.set(element, {
        opacity: 1,
        scale: 1,
        x: 0,
        clearProps: 'all',
      });
    }
  }, []);

  useEffect(() => {
    if (previousKeyRef.current === null) {
      previousKeyRef.current = swapKey;
      previousChildrenRef.current = children;
      setCurrentChildren(children);
      return;
    }

    if (previousKeyRef.current === swapKey) {
      previousChildrenRef.current = children;
      setCurrentChildren(children);
      return;
    }

    // Kill any existing animation when a new swap is triggered
    if (timelineRef.current) {
      timelineRef.current.kill();
      timelineRef.current = null;
    }

    if (isAnimatingRef.current) {
      const newElement = newLayerRef.current?.firstElementChild as HTMLElement;
      resetToFinalState(newElement);
      setOldChildren(null);
    }

    isAnimatingRef.current = false;

    Promise.resolve().then(() => {
      setOldChildren(previousChildrenRef.current);
      previousChildrenRef.current = children;
      setCurrentChildren(children);
      shouldAnimateRef.current = true;
    });

    return () => {
      if (timelineRef.current) {
        timelineRef.current.kill();
        timelineRef.current = null;
      }
      isAnimatingRef.current = false;
    };
  }, [swapKey, children, resetToFinalState]);

  useIsomorphicLayoutEffect(() => {
    if (previousKeyRef.current === null) {
      previousKeyRef.current = swapKey;
      if (newLayerRef.current) {
        const element = newLayerRef.current.firstElementChild as HTMLElement;
        if (element) {
          gsap.set(element, {
            opacity: 1,
            scale: 1,
            x: 0,
          });
        }
      }
      return;
    }

    if (!shouldAnimateRef.current || !oldChildren || isAnimatingRef.current) {
      return;
    }

    const oldElement = oldLayerRef.current?.firstElementChild as HTMLElement;
    const newElement = newLayerRef.current?.firstElementChild as HTMLElement;

    if (!oldElement || !newElement) {
      previousKeyRef.current = swapKey;
      shouldAnimateRef.current = false;
      return;
    }

    // Ensure any existing timeline is killed
    if (timelineRef.current) {
      timelineRef.current.kill();
      timelineRef.current = null;
    }

    isAnimatingRef.current = true;
    shouldAnimateRef.current = false;

    // Reset elements to their starting positions
    gsap.set(oldElement, {
      opacity: 1,
      scale: 1,
      x: 0,
    });

    gsap.set(newElement, {
      opacity: 0,
      scale: SWAP_ENTER_SCALE_START,
      x: SWAP_ENTER_X_OFFSET,
    });

    const currentSwapKey = swapKey;

    const tl = gsap.timeline({
      onComplete: () => {
        previousKeyRef.current = currentSwapKey;
        setOldChildren(null);
        isAnimatingRef.current = false;
        timelineRef.current = null;
        // Clear any leftover transforms
        resetToFinalState(newElement);
      },
      onInterrupt: () => {
        // When interrupted, immediately clean up to prevent frozen state
        isAnimatingRef.current = false;
        timelineRef.current = null;
        // Reset the new element to fully visible state
        resetToFinalState(newElement);
        // Clear old children to prevent overlap
        setOldChildren(null);
        // Update the key reference
        previousKeyRef.current = currentSwapKey;
      },
    });

    timelineRef.current = tl;

    tl.to(oldElement, {
      opacity: 0,
      scale: SWAP_EXIT_SCALE,
      x: SWAP_EXIT_X_OFFSET,
      duration: SWAP_EXIT_DURATION,
      ease: 'power2.in',
    });

    tl.to(
      newElement,
      {
        opacity: 1,
        scale: SWAP_ENTER_SCALE_END,
        x: 0,
        duration: SWAP_ENTER_DURATION,
        ease: 'power2.out',
      },
      SWAP_EXIT_DURATION * 0.6, // Start enter animation 60% into the exit animation (after fade-out is mostly complete)
    );

    return () => {
      if (timelineRef.current) {
        timelineRef.current.kill();
        timelineRef.current = null;
      }
      isAnimatingRef.current = false;
    };
  }, [swapKey, oldChildren, resetToFinalState]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timelineRef.current) {
        timelineRef.current.kill();
        timelineRef.current = null;
      }
      isAnimatingRef.current = false;
    };
  }, []);

  return (
    <div className={cx(styles.swapContainer, className)}>
      {oldChildren && (
        <div className={styles.swapLayer} ref={oldLayerRef}>
          {oldChildren}
        </div>
      )}
      <div className={styles.swapLayer} ref={newLayerRef}>
        {currentChildren}
      </div>
    </div>
  );
};

const ProductDetails: React.FC = () => {
  const { variantData, selectedVariant } = useVariantSelection();
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const contentRefs = useRef<(HTMLDivElement | null)[]>([]);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const iconRefs = useRef<(HTMLSpanElement | null)[]>([]);
  const openHeightsRef = useRef<number[]>([]);
  const isAnimatingRef = useRef(false);
  const accordionTimelineRef = useRef<gsap.core.Timeline | null>(null);

  useEffect(() => {
    // Kill any ongoing accordion animation when variant changes
    if (accordionTimelineRef.current) {
      accordionTimelineRef.current.kill();
      accordionTimelineRef.current = null;
    }
    isAnimatingRef.current = false;

    setOpenIndex((currentOpenIndex) => {
      if (currentOpenIndex === null) return null;
      if (currentOpenIndex >= variantData.accordionItems.length) {
        const content = contentRefs.current[currentOpenIndex];
        const item = itemRefs.current[currentOpenIndex];
        const icon = iconRefs.current[currentOpenIndex];
        if (content) {
          gsap.set(content, { height: CLOSED_HEIGHT, paddingBottom: 0 });
        }
        if (item) {
          gsap.set(item, { backgroundColor: '#535353' });
        }
        if (icon) {
          icon.textContent = '+';
        }
        return null;
      }
      return currentOpenIndex;
    });
  }, [selectedVariant, variantData.accordionItems.length]);

  // Cleanup accordion animations on unmount
  useEffect(() => {
    return () => {
      if (accordionTimelineRef.current) {
        accordionTimelineRef.current.kill();
        accordionTimelineRef.current = null;
      }
      isAnimatingRef.current = false;
    };
  }, []);

  useEffect(() => {
    const measureHeights = () => {
      openHeightsRef.current = contentRefs.current.map((ref) => {
        if (!ref) return 0;
        const currentHeight = ref.style.height;
        ref.style.height = 'auto';
        const height = ref.scrollHeight + HEIGHT_BUFFER;
        ref.style.height = currentHeight;
        return height;
      });
    };

    const timeout = setTimeout(measureHeights, 100);
    return () => clearTimeout(timeout);
  }, [variantData.accordionItems]);

  const getImageUrl = useCallback((path: string) => {
    if (path.startsWith('http://') || path.startsWith('https://')) {
      return path;
    }
    return getCompressedAssetUrl(path);
  }, []);

  useEffect(() => {
    const preloadImages = () => {
      const imagesToPreload: string[] = [];

      if (variantData.modelImagePath) {
        imagesToPreload.push(getImageUrl(variantData.modelImagePath));
      }

      variantData.accordionItems.forEach((item) => {
        if (item.image) {
          imagesToPreload.push(getImageUrl(item.image));
        }
      });

      const imagePromises = imagesToPreload.map((url) => {
        return new Promise<void>((resolve, reject) => {
          const img = new Image();
          img.onload = () => resolve();
          img.onerror = () => reject(new Error(`Failed to load image: ${url}`));
          img.src = url;
        });
      });

      Promise.allSettled(imagePromises).then((results) => {
        results.forEach((result, index) => {
          if (result.status === 'rejected') {
            console.warn(`Failed to preload image: ${imagesToPreload[index]}`);
          }
        });
      });
    };

    preloadImages();
  }, [variantData.modelImagePath, variantData.accordionItems]);

  const handleAccordionToggle = (index: number) => {
    // Kill any existing animation before starting a new one
    if (accordionTimelineRef.current) {
      accordionTimelineRef.current.kill();
      accordionTimelineRef.current = null;
    }

    const wasOpen = openIndex === index;
    const newOpenIndex = wasOpen ? null : index;
    const previousOpenIndex = openIndex;

    const isSwapping =
      previousOpenIndex !== null &&
      newOpenIndex !== null &&
      previousOpenIndex !== newOpenIndex;
    const isOpeningFirst = previousOpenIndex === null && newOpenIndex !== null;
    const isClosingLast = previousOpenIndex !== null && newOpenIndex === null;

    isAnimatingRef.current = true;
    setOpenIndex(newOpenIndex);

    const tl = gsap.timeline({
      onComplete: () => {
        isAnimatingRef.current = false;
        accordionTimelineRef.current = null;
      },
      onInterrupt: () => {
        isAnimatingRef.current = false;
        accordionTimelineRef.current = null;
      },
    });

    accordionTimelineRef.current = tl;

    if (isSwapping && previousOpenIndex !== null) {
      const prevContent = contentRefs.current[previousOpenIndex];
      const prevItem = itemRefs.current[previousOpenIndex];
      const prevIcon = iconRefs.current[previousOpenIndex];

      const newContent = contentRefs.current[index];
      const newItem = itemRefs.current[index];
      const newIcon = iconRefs.current[index];
      const newHeight = openHeightsRef.current[index] || 0;

      tl.to(prevContent, {
        height: CLOSED_HEIGHT,
        duration: ANIMATION_DURATION,
        ease: 'none',
      })
        .to(
          prevContent,
          {
            paddingBottom: 0,
            duration: ANIMATION_DURATION,
            ease: 'power2.out',
          },
          0,
        )
        .to(
          prevItem,
          {
            backgroundColor: '#535353',
            duration: ANIMATION_DURATION,
            ease: 'power2.out',
          },
          0,
        )
        .call(
          () => {
            if (prevIcon) {
              prevIcon.textContent = '+';
            }
          },
          [],
          0,
        )
        .to(
          newContent,
          {
            height: newHeight,
            duration: ANIMATION_DURATION,
            ease: 'none',
          },
          0,
        )
        .to(
          newContent,
          {
            paddingBottom:
              window.innerWidth >= DESKTOP_BREAKPOINT
                ? OPEN_PADDING_BOTTOM_DESKTOP
                : OPEN_PADDING_BOTTOM_MOBILE,
            duration: ANIMATION_DURATION,
            ease: 'power2.out',
          },
          0,
        )
        .to(
          newItem,
          {
            backgroundColor: '#000000',
            duration: ANIMATION_DURATION,
            ease: 'power2.out',
          },
          0,
        )
        .call(
          () => {
            if (newIcon) {
              newIcon.textContent = '−';
            }
          },
          [],
          0,
        );
    } else if (isOpeningFirst) {
      const content = contentRefs.current[index];
      const item = itemRefs.current[index];
      const icon = iconRefs.current[index];
      const height = openHeightsRef.current[index] || 0;

      tl.to(content, {
        height: height,
        duration: ANIMATION_DURATION,
        ease: 'none',
      })
        .to(
          content,
          {
            paddingBottom:
              window.innerWidth >= DESKTOP_BREAKPOINT
                ? OPEN_PADDING_BOTTOM_DESKTOP
                : OPEN_PADDING_BOTTOM_MOBILE,
            duration: ANIMATION_DURATION,
            ease: 'power2.out',
          },
          0,
        )
        .to(
          item,
          {
            backgroundColor: '#000000',
            duration: ANIMATION_DURATION,
            ease: 'power2.out',
          },
          0,
        )
        .call(
          () => {
            if (icon) {
              icon.textContent = '−';
            }
          },
          [],
          0,
        );
    } else if (isClosingLast && previousOpenIndex !== null) {
      const content = contentRefs.current[previousOpenIndex];
      const item = itemRefs.current[previousOpenIndex];
      const icon = iconRefs.current[previousOpenIndex];

      tl.to(content, {
        height: CLOSED_HEIGHT,
        duration: ANIMATION_DURATION,
        ease: 'none',
      })
        .to(
          content,
          {
            paddingBottom: 0,
            duration: ANIMATION_DURATION,
            ease: 'power2.out',
          },
          0,
        )
        .to(
          item,
          {
            backgroundColor: '#535353',
            duration: ANIMATION_DURATION,
            ease: 'power2.out',
          },
          0,
        )
        .call(
          () => {
            if (icon) {
              icon.textContent = '+';
            }
          },
          [],
          0,
        );
    }
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleAccordionToggle(index);
    }
  };

  const currentImage =
    openIndex !== null ? variantData.accordionItems[openIndex].image : null;

  const imageToShow =
    currentImage && openIndex !== null
      ? getImageUrl(currentImage)
      : getImageUrl(variantData.modelImagePath);
  const imageAlt =
    currentImage && openIndex !== null
      ? variantData.accordionItems[openIndex].title
      : '3D Model Placeholder';
  const imageSwapKey =
    currentImage && openIndex !== null
      ? `accordion-${openIndex}-${currentImage}`
      : `model-${variantData.modelImagePath}`;

  return (
    <section className={styles.section}>
      <div className={styles.headingWrapper}>
        <Heading $color="d_red">DIVE INTO THE DETAILS</Heading>
      </div>
      <ContainerLG className={styles.contentWrapper}>
        <div className={styles.imageSection}>
          <AnimatedSwap swapKey={imageSwapKey}>
            <img
              className={styles.swapImage}
              src={imageToShow}
              alt={imageAlt}
              loading="lazy"
            />
          </AnimatedSwap>
        </div>
        <div className={styles.accordionSection}>
          <div className={styles.accordionList}>
            {variantData.accordionItems.map((item, index) => {
              const isOpen = openIndex === index;
              return (
                <div className={styles.accordionItemWrapper} key={index}>
                  <div
                    className={styles.accordionItem}
                    ref={(el) => {
                      itemRefs.current[index] = el;
                    }}
                  >
                    <button
                      className={styles.accordionButton}
                      ref={(el) => {
                        buttonRefs.current[index] = el;
                      }}
                      id={`accordion-button-${index}`}
                      onClick={() => handleAccordionToggle(index)}
                      onKeyDown={(e) => handleKeyDown(e, index)}
                      aria-expanded={isOpen}
                      aria-controls={`accordion-content-${index}`}
                      aria-label={`${item.title} accordion, ${
                        isOpen ? 'expanded' : 'collapsed'
                      }`}
                    >
                      <h4 className={styles.accordionTitle}>{item.title}</h4>
                      <span
                        className={styles.plusIcon}
                        ref={(el) => {
                          iconRefs.current[index] = el;
                        }}
                      >
                        {isOpen ? '−' : '+'}
                      </span>
                    </button>
                    <div
                      className={styles.accordionContent}
                      ref={(el) => {
                        contentRefs.current[index] = el;
                      }}
                      id={`accordion-content-${index}`}
                      role="region"
                      aria-labelledby={`accordion-button-${index}`}
                    >
                      <p className={styles.accordionSubtitle}>
                        {item.subtitle}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </ContainerLG>
    </section>
  );
};

export default ProductDetails;
